import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList, signal, ElementRef, ViewChild, effect, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZnTabComponent } from '../zn-tab/zn-tab';

@Component({
  selector: 'zn-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './zn-tabs.html',
  styleUrls: ['./zn-tabs.scss']
})
export class ZnTabsComponent implements AfterContentInit {
  @Input() name = 'zn-tabs';
  activeTabName = input<string | null>(null);
  @ContentChildren(ZnTabComponent) tabsList!: QueryList<ZnTabComponent>;
  @Output() tabChanged = new EventEmitter<ZnTabComponent>();
  @ViewChild('tabsContainer', { static: false }) tabsContainer?: ElementRef<HTMLElement>;

  activeTab = signal<ZnTabComponent | null>(null);

  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;

  constructor() {
    // Sync activeTab with parent's activeTabName input
    effect(() => {
      const tabName = this.activeTabName();
      if (tabName && this.tabsList) {
        const matchingTab = this.tabsList.find(tab => tab.name === tabName);
        if (matchingTab && this.activeTab() !== matchingTab) {
          this.activeTab.set(matchingTab);
        }
      }
    });
  }

  ngAfterContentInit(): void {
    // If parent provides activeTabName, use it; otherwise default to first tab
    const tabName = this.activeTabName();
    let active: ZnTabComponent | undefined;

    if (tabName) {
      active = this.tabsList.find(tab => tab.name === tabName);
    }

    if (!active) {
      active = this.tabsList.find(tab => tab.active) || this.tabsList.first;
    }

    this.activeTab.set(active);
  }

  selectTab(tab: ZnTabComponent) {
    if (!tab.disabled) {
      this.activeTab.set(tab);
      this.tabChanged.emit(tab);
    }
  }

  isActive(tab: ZnTabComponent) {
    return this.activeTab() === tab;
  }

  idOf(tab: ZnTabComponent) {
    return `${this.name}-${tab.name}`;
  }

  btnId(tab: ZnTabComponent) {
    return `${this.idOf(tab)}-tab`;
  }

  onMouseDown(e: MouseEvent) {
    if (!this.tabsContainer) return;

    this.isDragging = true;
    const container = this.tabsContainer.nativeElement;
    this.startX = e.pageX - container.offsetLeft;
    this.scrollLeft = container.scrollLeft;
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
  }

  onMouseLeave() {
    this.isDragging = false;
    if (this.tabsContainer) {
      this.tabsContainer.nativeElement.style.cursor = 'grab';
      this.tabsContainer.nativeElement.style.userSelect = 'auto';
    }
  }

  onMouseUp() {
    this.isDragging = false;
    if (this.tabsContainer) {
      this.tabsContainer.nativeElement.style.cursor = 'grab';
      this.tabsContainer.nativeElement.style.userSelect = 'auto';
    }
  }

  onMouseMove(e: MouseEvent) {
    if (!this.isDragging || !this.tabsContainer) return;

    e.preventDefault();
    const container = this.tabsContainer.nativeElement;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - this.startX) * 2;
    container.scrollLeft = this.scrollLeft - walk;
  }
}